// Reason: Structured logging needs should be able to take in anything.
/* eslint-disable @typescript-eslint/no-explicit-any */

import { Disposer, EventEmitter, isSomeError } from '@visiba/std';

export class Logger {
	private static runtime: Runtime | null = null; // TODO: Nullable<Runtime>
	public static attachChannel(channel: Channel): void {
		this.runtime ??= new Runtime();

		Logger.runtime?.attachChannel(channel);
	}

	public static on(event: Events, cb: (data: EventDispatchedData) => void): Disposer {
		if (Logger.runtime == null) throw new Error('Logger runtime is not initialized');

		return Logger.runtime.on(event, cb);
	}

	public static off(event: Events, cb: (data: EventDispatchedData) => void): void {
		if (Logger.runtime == null) throw new Error('Logger runtime is not initialized');

		Logger.runtime.off(event, cb);
	}

	constructor(private readonly ctx: string | null = null) {
		// Empty
	}

	/**
	 * Fine-grained trace information describing exactly what’s happening in the application, as a tool for debugging.
	 */

	// biome-ignore lint/suspicious/noExplicitAny: suppressed due to migration - should be fixed
	public logVerbose(strTemplate: string, ...replaceArgs: any[]): void {
		this.createMessage(LogLevel.Verbose, strTemplate, ...replaceArgs);
	}

	/**
	 * Less fine-grained than the Verbose level, as it does not “trace” progress,
	 * but instead injects useful “debug information” at important points in the application.
	 */

	// biome-ignore lint/suspicious/noExplicitAny: suppressed due to migration - should be fixed
	public logDebug(strTemplate: string, ...replaceArgs: any[]): void {
		this.createMessage(LogLevel.Debug, strTemplate, ...replaceArgs);
	}

	/**
	 * Events of note from a service/platform perspective.
	 */

	// biome-ignore lint/suspicious/noExplicitAny: suppressed due to migration - should be fixed
	public logInformation(strTemplate: string, ...replaceArgs: any[]): void {
		this.createMessage(LogLevel.Information, strTemplate, ...replaceArgs);
	}

	/**
	 * Something that needs tech/developer attention, but isn’t acute or a hindrance to continued operation.
	 */

	// biome-ignore lint/suspicious/noExplicitAny: suppressed due to migration - should be fixed
	public logWarning(strTemplate: string, ...replaceArgs: any[]): void {
		this.createMessage(LogLevel.Warning, strTemplate, ...replaceArgs);
	}

	/**
	 * Similar to Warning, but this event resulted in an unrecoverable action.
	 * These are typically of high priority to attend to, as they may completely hinder users from using a certain feature.
	 */

	// biome-ignore lint/suspicious/noExplicitAny: suppressed due to migration - should be fixed
	public logError(strTemplate: string, ...replaceArgs: any[]): void {
		this.createMessage(LogLevel.Error, strTemplate, ...replaceArgs);
	}

	/**
	 * An event that is cause for alarm and acute intervention. The entire service or an important component
	 * of the service fails to do its task. Leaving all related operations to fail.
	 */

	// biome-ignore lint/suspicious/noExplicitAny: suppressed due to migration - should be fixed
	public logFatal(strTemplate: string, ...replaceArgs: any[]): void {
		this.createMessage(LogLevel.Fatal, strTemplate, ...replaceArgs);
	}

	private createMessage(level: LogLevel, template: string, ...replaceArgs: string[]): void {
		if (Logger.runtime == null) return;

		const message = {
			timestamp: Date.now(),
			ctx: this.ctx,
			level,
			template,
			data: replaceArgs,
		};

		Logger.runtime.enqueue(message);
	}
}

// prettier-ignore
export enum LogLevel {
	Verbose = 1 << 6,
	Debug = 1 << 5,
	Information = 1 << 4,
	Warning = 1 << 3,
	Error = 1 << 2,
	Fatal = 1 << 1,
	None = 0,
}

export interface Channel {
	out(str: string, msgRaw: Message): void;
}

/**
 * Built-in channel that writes to the console.
 */
export class StdOutChannel implements Channel {
	out(str: string, msgRaw: Message) {
		switch (msgRaw.level) {
			case LogLevel.Error:
			case LogLevel.Fatal: {
				console.error('%c ' + str, 'color: rgb(255, 117, 117)');
				break;
			}
			case LogLevel.Warning: {
				console.warn('%c ' + str, 'color: rgb(255, 182, 0)');
				break;
			}
			case LogLevel.Verbose: {
				console.log('%c ' + str, 'color: rgb(170, 170, 170)');
				break;
			}
			case LogLevel.Debug: {
				console.log('%c ' + str, 'color: rgb(0, 255, 164)');
				break;
			}
			default:
				console.log('%c ' + str, 'color: #1ca8fa');
		}
	}
}

export type Events = Lowercase<keyof typeof LogLevel>;
export type EventDispatchedData = [parsedMsg: string, msg: Message];

class Runtime extends EventEmitter<Record<Events, EventDispatchedData>> {
	private messages: Message[] = [];
	private isScheduledToPrint = false;
	private printIndex = 0;

	public level = LogLevel.Verbose;
	private channel: Channel | null = null; // TODO: Nullable<Channel>

	public attachChannel(channel: Channel): void {
		this.channel = channel;
	}

	private print(parsedMsg: string, msg: Message): void {
		this.channel?.out(parsedMsg, msg);
	}

	private dispatchEvent(parsedMsg: string, msg: Message): void {
		const level = LogLevel[msg.level].toLowerCase() as Lowercase<keyof typeof LogLevel>;
		this.emit(level, [parsedMsg, msg]);
	}

	public enqueue(msg: Message): void {
		this.messages.push(msg);

		if (this.isScheduledToPrint) return;

		this.isScheduledToPrint = true;

		queueMicrotask(() => {
			this.walk();
			this.isScheduledToPrint = false;
		});
	}

	private walk(): void {
		while (this.printIndex < this.messages.length) {
			const msg = this.messages[this.printIndex];
			this.printIndex++;

			if (msg.level > this.level) return;

			const parsedMsg = this.parseMessage(msg);
			this.dispatchEvent(parsedMsg, msg);
			this.print(parsedMsg, msg);
		}
	}

	private parseMessage(msg: Message): string {
		let extractedDataIndex = 0;

		let result = '';
		let walkerIndex = 0;
		while (walkerIndex < msg.template.length) {
			const openBraceIndex = msg.template.indexOf('{', walkerIndex);
			if (openBraceIndex === -1) {
				result += msg.template.slice(walkerIndex);
				break;
			}

			const closeBraceIndex = msg.template.indexOf('}', openBraceIndex + 1);
			if (closeBraceIndex === -1) {
				result += msg.template.slice(walkerIndex);
				break;
			}

			result += msg.template.slice(walkerIndex, openBraceIndex);
			// const key = msg.template.slice(openBraceIndex + 1, closeBraceIndex);

			const data = this.unwrapData(msg.data[extractedDataIndex]);

			result += data;
			extractedDataIndex++;

			walkerIndex = closeBraceIndex + 1;
		}

		return ['[' + new Date(msg.timestamp).toLocaleTimeString(navigator.language) + ']', this.getCtxSection(msg), result]
			.filter(Boolean)
			.join(' ');
	}

	// biome-ignore lint/suspicious/noExplicitAny: suppressed due to migration - should be fixed
	private unwrapData(someValue: any): string {
		if (isSomeError(someValue)) {
			return someValue.message;
		}

		return `${someValue}`;
	}

	private getCtxSection(msg: Message): string | null {
		if (!msg.ctx) return null;

		return '[' + msg.ctx + ']';
	}
}

export interface Message {
	timestamp: number;
	ctx: string | null;
	level: LogLevel;
	template: string;
	data: string[];
}
